'use client';

import { ErrorBoundary } from '@datadog/browser-rum-react';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ThemeProvider } from 'next-themes';
import { type ReactNode } from 'react';

import { queryClient } from '@/lib/api';

import Amplitude from './amplitude';
import DatadogIdentify from './datadog-identify';
import DatadogInit from './datadog-rum';
import ImpersonationBanner from './impersonation-banner';
import PostHogIdentify from './posthog-identify';
import { Toaster } from './ui/sonner';
import { TooltipProvider } from './ui/tooltip';

/**
 * Auth Provider Component
 *
 * Note: Better Auth React client doesn't require a provider.
 * Session state is managed globally via nanostores.
 * This component is kept for future enhancements.
 *
 * @example
 * ```tsx
 * <AuthProvider>
 *   <App />
 * </AuthProvider>
 * ```
 */
export function Providers({ children }: { children: ReactNode }) {
  return (
    <ThemeProvider
      attribute="class"
      defaultTheme="dark"
      forcedTheme="dark"
      disableTransitionOnChange
    >
      <QueryClientProvider client={queryClient}>
        <ErrorBoundary fallback={() => null}>
          <Amplitude />
          <PostHogIdentify />
          <DatadogIdentify />
          <DatadogInit />
          <ImpersonationBanner />
          <TooltipProvider delayDuration={0} skipDelayDuration={0}>
            {children}
            <ReactQueryDevtools />
          </TooltipProvider>
        </ErrorBoundary>
      </QueryClientProvider>
      <Toaster richColors position="bottom-left" />
    </ThemeProvider>
  );
}
